import { useCallback, useMemo, useState } from "react";

import { useSearchParamsState } from "./use-search-params";
import { useSearchModelConverter } from "../../converter";

import type * as Repository from "../../repository";
import type * as Type from "../../type";

type Props = {
	queryData: Repository.Query.QueryData.ReservationListQueryData;
};

export const useSearchModel = ({ queryData }: Props) => {
	const { onConvertSearchModel } = useSearchModelConverter();
	const { searchParamsState, updateSearchParamsState } = useSearchParamsState();
	/**
	 * state の初期値
	 */
	const { searchModel: initSearchModel } = useMemo(
		() => onConvertSearchModel({ queryData, searchParamsState }),
		[onConvertSearchModel, queryData, searchParamsState],
	);
	const [searchModel, setSearchModel] = useState<Type.SearchModel>(initSearchModel);

	/**
	 * 詳細検索のフリーワードテキストの変更（空白区切り）
	 */
	const onChangeFreeWordText = useCallback((value: string) => {
		setSearchModel((prev) => ({ ...prev, freeWordText: value }));
	}, []);
	/**
	 * 詳細検索のアクティブな予約フラグの変更
	 */
	const onChangeIsActiveChecked = useCallback(
		(value: boolean) => {
			setSearchModel((prev) => ({ ...prev, isActiveChecked: value }));
			updateSearchParamsState({ isActiveChecked: value });
		},
		[updateSearchParamsState],
	);
	/**
	 * 詳細検索のキャンセル済みフラグの変更
	 */
	const onChangeIsCanceledChecked = useCallback(
		(value: boolean) => {
			setSearchModel((prev) => ({ ...prev, isCanceledChecked: value }));
			updateSearchParamsState({ isCanceledChecked: value });
		},
		[updateSearchParamsState],
	);
	/**
	 * 詳細検索のメルマガ配信希望者フラグの変更
	 */
	const onChangeIsAcceptMailMagazineChecked = useCallback(
		(value: boolean) => {
			setSearchModel((prev) => ({ ...prev, isAcceptMailMagazineChecked: value }));
			updateSearchParamsState({ isAcceptMailMagazineChecked: value });
		},
		[updateSearchParamsState],
	);
	/**
	 * 詳細検索の現地決済者フラグの変更
	 */
	const onChangeIsLocalPaymentChecked = useCallback(
		(value: boolean) => {
			setSearchModel((prev) => ({ ...prev, isLocalPaymentChecked: value }));
			updateSearchParamsState({ isLocalPaymentChecked: value });
		},
		[updateSearchParamsState],
	);
	/**
	 * 詳細検索の直接入力した予約フラグの変更
	 */
	const onChangeIsHostReservationChecked = useCallback(
		(value: boolean) => {
			setSearchModel((prev) => ({ ...prev, isHostReservationChecked: value }));
			updateSearchParamsState({ isHostReservationChecked: value });
		},
		[updateSearchParamsState],
	);
	/**
	 * 詳細検索の日帰り予約フラグの変更
	 */
	const onChangeIsDayTripChecked = useCallback(
		(value: boolean) => {
			setSearchModel((prev) => ({ ...prev, isDayTripChecked: value }));
			updateSearchParamsState({ isDayTripChecked: value });
		},
		[updateSearchParamsState],
	);
	/**
	 * 詳細検索の予約内容/プランでの検索条件の追加
	 */
	const onAddReservationPlans = useCallback(
		(planId: string) => {
			const newPlans = searchModel.reservation.plans.map((plan) => ({
				...plan,
				checked: plan.id === planId ? true : plan.checked,
			}));

			setSearchModel((prev) => ({
				...prev,
				reservation: {
					...prev.reservation,
					plans: newPlans,
				},
			}));

			updateSearchParamsState({ reservation: { ...searchModel.reservation, plans: newPlans } });
		},
		[searchModel.reservation, updateSearchParamsState],
	);
	/**
	 * 詳細検索の予約内容/プランでの検索条件の削除
	 */
	const onRemoveReservationPlans = useCallback(
		(planId: string) => {
			const newPlans = searchModel.reservation.plans.map((plan) => ({
				...plan,
				checked: plan.id === planId ? false : plan.checked,
			}));

			setSearchModel((prev) => ({
				...prev,
				reservation: {
					...prev.reservation,
					plans: newPlans,
				},
			}));

			updateSearchParamsState({ reservation: { ...searchModel.reservation, plans: newPlans } });
		},
		[searchModel.reservation, updateSearchParamsState],
	);
	/**
	 * 詳細検索の予約内容/部屋での検索条件の追加
	 */
	const onAddReservationRooms = useCallback(
		(roomId: string) => {
			setSearchModel((prev) => {
				const newRooms = prev.reservation.rooms.map((room) => ({
					...room,
					checked: room.id === roomId ? true : room.checked,
				}));

				updateSearchParamsState({ reservation: { ...prev.reservation, rooms: newRooms } });

				return {
					...prev,
					reservation: {
						...prev.reservation,
						rooms: newRooms,
					},
				};
			});
		},
		[updateSearchParamsState],
	);
	/**
	 * 詳細検索の予約内容/部屋での検索条件の削除
	 */
	const onRemoveReservationRooms = useCallback(
		(roomId: string) => {
			setSearchModel((prev) => {
				const newRooms = prev.reservation.rooms.map((room) => ({
					...room,
					checked: room.id === roomId ? false : room.checked,
				}));

				updateSearchParamsState({ reservation: { ...prev.reservation, rooms: newRooms } });

				return {
					...prev,
					reservation: {
						...prev.reservation,
						rooms: newRooms,
					},
				};
			});
		},
		[updateSearchParamsState],
	);
	/**
	 * 詳細検索の予約内容/オプションでの検索条件の追加
	 */
	const onAddReservationOptions = useCallback(
		(optionId: string) => {
			setSearchModel((prev) => {
				const newOptions = prev.reservation.options.map((option) => ({
					...option,
					checked: option.id === optionId ? true : option.checked,
				}));

				updateSearchParamsState({ reservation: { ...prev.reservation, options: newOptions } });

				return {
					...prev,
					reservation: {
						...prev.reservation,
						options: newOptions,
					},
				};
			});
		},
		[updateSearchParamsState],
	);
	/**
	 * 詳細検索の予約内容/オプションでの検索条件の削除
	 */
	const onRemoveReservationOptions = useCallback(
		(optionId: string) => {
			setSearchModel((prev) => {
				const newOptions = prev.reservation.options.map((option) => ({
					...option,
					checked: option.id === optionId ? false : option.checked,
				}));

				updateSearchParamsState({ reservation: { ...prev.reservation, options: newOptions } });

				return {
					...prev,
					reservation: {
						...prev.reservation,
						options: newOptions,
					},
				};
			});
		},
		[updateSearchParamsState],
	);

	/**
	 * 詳細検索の予約内容/ディスカウントでの検索条件の追加
	 */
	const onAddReservationDiscounts = useCallback(
		(discountId: string) => {
			setSearchModel((prev) => {
				const newDiscounts = prev.reservation.discounts.map((discount) => ({
					...discount,
					checked: discount.id === discountId ? true : discount.checked,
				}));

				updateSearchParamsState({ reservation: { ...prev.reservation, discounts: newDiscounts } });

				return {
					...prev,
					reservation: {
						...prev.reservation,
						discounts: newDiscounts,
					},
				};
			});
		},
		[updateSearchParamsState],
	);
	/**
	 * 詳細検索の予約内容/ディスカウントでの検索条件の削除
	 */
	const onRemoveReservationDiscounts = useCallback(
		(discountId: string) => {
			setSearchModel((prev) => {
				const newDiscounts = prev.reservation.discounts.map((discount) => ({
					...discount,
					checked: discount.id === discountId ? false : discount.checked,
				}));

				updateSearchParamsState({ reservation: { ...prev.reservation, discounts: newDiscounts } });

				return {
					...prev,
					reservation: {
						...prev.reservation,
						discounts: newDiscounts,
					},
				};
			});
		},
		[updateSearchParamsState],
	);

	return {
		//
		searchModel,
		//
		// 詳細検索のフリーワードテキストの変更（空白区切り）
		onChangeFreeWordText,
		// 詳細検索のアクティブな予約フラグの変更
		onChangeIsActiveChecked,
		// 詳細検索のキャンセル済みフラグの変更
		onChangeIsCanceledChecked,
		// 詳細検索のメルマガ配信希望者フラグの変更
		onChangeIsAcceptMailMagazineChecked,
		// 詳細検索の現地決済者フラグの変更
		onChangeIsLocalPaymentChecked,
		// 詳細検索の直接入力した予約フラグの変更
		onChangeIsHostReservationChecked,
		// 詳細検索の日帰り予約フラグの変更
		onChangeIsDayTripChecked,
		// 詳細検索の予約内容/プランでの検索条件の追加
		onAddReservationPlans,
		// 詳細検索の予約内容/プランでの検索条件の削除
		onRemoveReservationPlans,
		// 詳細検索の予約内容/部屋での検索条件の追加
		onAddReservationRooms,
		// 詳細検索の予約内容/部屋での検索条件の削除
		onRemoveReservationRooms,
		// 詳細検索の予約内容/オプションでの検索条件の追加
		onAddReservationOptions,
		// 詳細検索の予約内容/オプションでの検索条件の削除
		onRemoveReservationOptions,
		// 詳細検索の予約内容/ディスカウントでの検索条件の追加
		onAddReservationDiscounts,
		// 詳細検索の予約内容/ディスカウントでの検索条件の削除
		onRemoveReservationDiscounts,
	};
};
